import Page from "../../components/page";
import React from "react";
import {
  Body1,
  Heading1,
} from "@vorne/gatsby-theme-marketing/components/typography/typography";
import Grid from "@vorne/gatsby-theme-marketing/components/layout/grid/grid";
import Image from "@vorne/gatsby-theme-marketing/components/image/image";
import { graphql } from "gatsby";
import QuickLinks from "@vorne/gatsby-theme-marketing/components/quicklinks/quickLinks";
import PageTitle from "@vorne/gatsby-theme-marketing/components/pagetitle/pageTitle";
import List, {
  ListItem,
} from "@vorne/gatsby-theme-marketing/components/list/v1";
import Section from "@vorne/gatsby-theme-marketing/components/section/section";
import Link from "@vorne/gatsby-theme-marketing/components/link/link";
import { Seo } from "@vorne/gatsby-theme-marketing/components/seo/v2";

const IntegrationTools = ({ data }) => {
  return (
    <Page>
      <Section>
        <PageTitle>Integration Tools</PageTitle>
        <QuickLinks tag="h2" />
        <Heading1 id="overview">Overview</Heading1>
        <Body1>
          A great way to leverage XL and streamline your operations is to
          integrate XL with your other manufacturing systems.
        </Body1>
        <Body1>
          XL has been designed to work with almost any type of PLC, MES, ERP, or
          Reporting system. You integrate XL with your process using our API, or
          by using a solution provided by a third-party integration partner.
        </Body1>
        <Body1>
          Our integration partners help you to quickly integrate XL by combining
          an extremely deep knowledge of XL with standard software tools, and
          custom integration services. They typically help people to:
        </Body1>
        <List type="bullet">
          <ListItem>Import XL data into existing reporting systems.</ListItem>
          <ListItem>
            Rollup, slice, dice, and analyze data across an entire enterprise.
          </ListItem>
          <ListItem>Link XL to their production scheduling system.</ListItem>
          <ListItem>Link XL to their PLCs to automate reason capture.</ListItem>
        </List>
        <Heading1 id="enterpriseDatabase">Enterprise Database</Heading1>
        <Grid $desktop="1 1">
          <div>
            <Body1>
              Using the Data Collector, automatically write data from multiple
              XL devices to an SQL Database on your server.
            </Body1>
            <List type="bullet">
              <ListItem>
                Archive your production data in an SQL Database.
              </ListItem>
              <ListItem>
                Eliminate manual reporting by integrating with your existing
                reporting solutions.
              </ListItem>
              <ListItem>
                Runs ‘behind the scenes’ as a simple automated windows service.
              </ListItem>
            </List>
            <Body1>
              Learn more about the XL Data Collector{" "}
              <Link url="/products/integration-tools/xl-bolt-on-data-collector-product-sheet.pdf">
                here
              </Link>
            </Body1>
          </div>
          <div>
            <Image
              src={data.productionDataDatabaseImage}
              alt="Image showing that Microsoft SQL can connect to XL."
            />
          </div>
        </Grid>
        <Heading1 id="eRPIntegration">ERP Integration</Heading1>
        <Grid $desktop="1 1">
          <div>
            <Body1>
              Using the Data Link ERP, establish a direct connection between
              your ERP system and XL systems to transfer job information from
              ERP into XL, and to transfer production data from XL back into
              ERP.
            </Body1>
            <List type="bullet">
              <ListItem>
                ERP knows your plan: Export production standards to XL.
              </ListItem>
              <ListItem>
                XL knows what you’re producing: Import production data from XL
                to your ERP system.
              </ListItem>
            </List>
          </div>
          <div>
            <Image
              src={data.erpXlLinkImage}
              alt="Image showing that in addition to SQL, many ERP systems can connect to XL."
            />
          </div>
        </Grid>
        <Heading1 id="xLHMI">XL HMI</Heading1>
        <Grid $desktop="1 1">
          <div>
            <Body1>
              The XL HMI connects one or more XL units to a single tablet,
              touchscreen, or PC, enabling your team to interact with XL from a
              screen on the plant floor. It can help your team:
            </Body1>
            <List type="bullet">
              <ListItem>
                Set downtime reason codes using a dropdown list or individual
                buttons for each reason.
              </ListItem>
              <ListItem>Enter reject reasons with dynamic quantities.</ListItem>
              <ListItem>
                Manually control production state and execute state changes such
                as Breaks/Lunch/Shift Start/Shift End.
              </ListItem>
              <ListItem>
                Execute pre-programmed parts and jobs or create new ones on the
                fly.
              </ListItem>
            </List>
            <Body1>
              Read more about the XL HMI{" "}
              <Link url="/products/integration-tools/xl-bolt-on-hmi-features.pdf">
                here
              </Link>
              .
            </Body1>
          </div>
          <div>
            <Image
              src={data.xlHmi}
              alt="Image showing that many XL devices can be connected to one screen using the XL HMI."
            />
          </div>
        </Grid>
        <Heading1 id="pLCIntegration">PLC Integration</Heading1>
        <Grid $desktop="1 1">
          <div>
            <Body1>
              The Data Link PLC moves data from your PLC to Vorne XL systems on
              the manufacturing floor. No programming required! Just browse tags
              and select different options for moving data in real-time.
            </Body1>
            <List type="bullet">
              <ListItem>
                Reduce the need to manually scan barcodes by sending information
                from your PLC to XL.
              </ListItem>
              <ListItem>
                Automatically activate down reasons or trigger various inputs to
                eliminate manual data collection.
              </ListItem>
              <ListItem>
                Send other information (numeric or alphanumeric) from the PLC to
                XL.
              </ListItem>
            </List>
          </div>
          <div>
            <Image
              src={data.plcDataLinkImage}
              alt="Image showing that a PLC can send information to XL."
            />
          </div>
        </Grid>
        <Heading1 id="xlCustomMessages">XL Custom Messages</Heading1>
        <Grid $desktop="1 1">
          <div>
            <Body1>
              Using PiXL software, create graphics and images on your computer
              for display on your Vorne XL system on-demand, or based on a
              specific metric.
            </Body1>
            <List type="bullet">
              <ListItem>
                Create custom messages such as “Great Job Team”, or graphics
                such as your company logo.
              </ListItem>
              <ListItem>
                Activate these graphics based on a specific metric – e.g.
                display Great Job Team when the crew beat their shift target.
              </ListItem>
            </List>
            <Body1>
              Read more about PiXL{" "}
              <Link url="/products/integration-tools/xl-bolt-on-pixl-product-sheet.pdf">
                here
              </Link>
              .
            </Body1>
          </div>
          <div>
            <Image
              src={data.xlCustomMessageImage}
              alt="Image showing that a message typed into a computer can appear on XL"
            />
          </div>
        </Grid>
        <Heading1 id="xlDisplayViewer">XL Display Viewer</Heading1>
        <Grid $desktop="1 1">
          <div>
            <Body1>
              Recreate the XL scoreboard on any monitor or television. See live
              data from multiple XLs on your desktop or LCD/Plasma TV displays
              in break rooms, hallways or anywhere.
            </Body1>
            <List type="bullet">
              <ListItem>
                Supports as many XL Devices as you have available on the
                network.
              </ListItem>
              <ListItem>Easy to deploy, with no programming needed.</ListItem>
            </List>
            <Body1>
              Read more about the XL Display Viewer{" "}
              <Link url="/products/integration-tools/xl-bolt-on-display-viewer-product-sheet.pdf">
                here
              </Link>
              .
            </Body1>
          </div>
          <div>
            <Image
              src={data.productionScoreboardMonitorImage}
              alt="Image showing that XL scoreboards can be viewed on a monitor."
            />
          </div>
        </Grid>
      </Section>
    </Page>
  );
};
export default IntegrationTools;

export function Head() {
  return (
    <Seo
      title="Vorne XL Integration Tools"
      description="Get tools to help you integrate the XL Productivity Appliance with your other manufacturing systems and streamline your operations."
    />
  );
}

export const query = graphql`
  query {
    productionDataDatabaseImage: file(
      relativePath: { eq: "production-data-database-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    erpXlLinkImage: file(relativePath: { eq: "erp-xl-link-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 916)
      }
    }
    xlHmi: file(relativePath: { eq: "xl-hmi.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED, width: 916)
      }
    }
    plcDataLinkImage: file(relativePath: { eq: "plc-data-link-image.png" }) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    xlCustomMessageImage: file(
      relativePath: { eq: "xl-custom-message-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
    productionScoreboardMonitorImage: file(
      relativePath: { eq: "production-scoreboard-monitor-image.png" }
    ) {
      childImageSharp {
        gatsbyImageData(placeholder: BLURRED)
      }
    }
  }
`;
